/**
 * Translate the technical errors thrown by `@simplewebauthn/browser`'s
 * `startAuthentication` / `startRegistration` into friendly, localized messages.
 *
 * Why this exists: the raw errors are English `DOMException`s (or SimpleWebAuthn
 * `WebAuthnError`s wrapping them). Showing `err.message` directly — e.g.
 * "The operation either timed out or was not allowed..." — is confusing,
 * especially on Windows where the same `NotAllowedError` covers "cancelled",
 * "timed out" and "no passkey for this site on this device".
 *
 * We map on two signals, most-specific first:
 *  1. SimpleWebAuthn's `code` (e.g. ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED)
 *  2. the underlying `DOMException.name` (e.g. NotAllowedError), found on the
 *     error itself or on its `cause`.
 */

export type WebAuthnContext = 'login' | 'register';

/** Pull SimpleWebAuthn's `code` off the error, if present. */
function webauthnCode(err: unknown): string | undefined {
    if (err && typeof err === 'object' && 'code' in err) {
        const code = (err as { code?: unknown }).code;
        if (typeof code === 'string' && code.startsWith('ERROR_')) {
            return code;
        }
    }
    return undefined;
}

/**
 * Resolve the underlying DOMException name. SimpleWebAuthn wraps the real
 * DOMException in `cause`; raw calls may throw the DOMException directly.
 */
function domExceptionName(err: unknown): string | undefined {
    if (!err || typeof err !== 'object') return undefined;

    const cause = (err as { cause?: unknown }).cause;
    if (cause instanceof Error && cause.name) {
        return cause.name;
    }

    const name = (err as { name?: unknown }).name;
    if (typeof name === 'string' && name && name !== 'WebAuthnError' && name !== 'Error') {
        return name;
    }

    return undefined;
}

export function webauthnErrorMessage(err: unknown, context: WebAuthnContext): string {
    const code = webauthnCode(err);

    switch (code) {
        case 'ERROR_CEREMONY_ABORTED':
            return 'Дію скасовано. Спробуйте ще раз.';
        case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
            return 'Цей пристрій уже зареєстровано для цього акаунта.';
        case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
            return 'Пристрій не підтримує перевірку особи (біометрія або PIN), яка обовʼязкова для ключа доступу.';
        case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
            return 'Пристрій не може зберегти ключ доступу. Спробуйте інший пристрій або апаратний ключ.';
        case 'ERROR_INVALID_DOMAIN':
        case 'ERROR_INVALID_RP_ID':
            return 'Помилка конфігурації домену для ключів доступу. Зверніться до підтримки.';
        // Both mean the browser/authenticator could not agree on a key algorithm
        // with the server's pubKeyCredParams (older browsers, some security keys).
        case 'ERROR_MALFORMED_PUBKEYCREDPARAMS':
        case 'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG':
            return 'Ваш браузер або пристрій не підтримує алгоритми, потрібні для ключа доступу. Оновіть браузер або спробуйте інший пристрій.';
        // ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY and the rest fall through to the
        // DOMException-name mapping below.
    }

    switch (domExceptionName(err)) {
        case 'NotAllowedError':
            return context === 'login'
                ? 'Не вдалося підтвердити ключ доступу. Можливо, дію скасовано, перевищено час очікування, або на цьому пристрої немає збереженого ключа цього акаунта.'
                : 'Створення ключа доступу скасовано або перевищено час очікування. Спробуйте ще раз.';
        case 'InvalidStateError':
            return context === 'register' ? 'Цей пристрій уже зареєстровано для цього акаунта.' : 'Ключ доступу недоступний на цьому пристрої.';
        case 'NotSupportedError':
            return 'Ваш браузер або пристрій не підтримує ключі доступу.';
        case 'SecurityError':
            return 'Помилка безпеки: адреса сайту не відповідає налаштуванням ключа доступу.';
        case 'AbortError':
            return 'Дію скасовано.';
        case 'ConstraintError':
            return 'Пристрій не може створити ключ доступу з потрібними вимогами (біометрія або PIN).';
    }

    return context === 'login'
        ? 'Не вдалося увійти за допомогою ключа доступу. Спробуйте ще раз.'
        : 'Не вдалося створити ключ доступу. Спробуйте ще раз.';
}
